/**
 * Analytics module for GA4 and Facebook Pixel integration.
 *
 * Both scripts are loaded asynchronously to avoid blocking page rendering.
 * Page view events fire on every route change via the usePageTracking hook.
 */

const GA4_TRACKING_ID = 'G-BFG7C61SMM';
const FB_PIXEL_ID = '1451068432350664';

/* ---------- Global type augmentation ---------- */

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    fbq: ((...args: unknown[]) => void) & { queue?: unknown[] };
    _fbq?: Window['fbq'];
  }
}

/* ---------- GA4 (gtag.js) ---------- */

let ga4Initialized = false;

/**
 * Loads the GA4 gtag.js script asynchronously and configures the tracking ID.
 * Safe to call multiple times; only initializes once.
 */
export function initGA4(): void {
  if (ga4Initialized || typeof document === 'undefined') return;
  ga4Initialized = true;

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA4_TRACKING_ID, { send_page_view: false });

  // Load script asynchronously
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_TRACKING_ID}`;
  script.async = true;
  document.head.appendChild(script);
}

/* ---------- Facebook Pixel ---------- */

let fbPixelInitialized = false;

/**
 * Loads the Facebook Pixel script asynchronously and initializes the pixel ID.
 * Safe to call multiple times; only initializes once.
 */
export function initFBPixel(): void {
  if (fbPixelInitialized || typeof document === 'undefined') return;
  fbPixelInitialized = true;

  // Initialize fbq stub
  const queue: unknown[] = [];
  const fbq = Object.assign(
    function fbq() {
      // eslint-disable-next-line prefer-rest-params
      queue.push(arguments);
    },
    { queue },
  ) as Window['fbq'];
  window.fbq = fbq;
  window._fbq = fbq;

  window.fbq('init', FB_PIXEL_ID);

  // Load script asynchronously
  const script = document.createElement('script');
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  script.async = true;
  document.head.appendChild(script);
}

/* ---------- Page view tracking ---------- */

/**
 * Fires a page_view event on both GA4 and Facebook Pixel.
 * Call this on every route change.
 */
export function trackPageView(path: string): void {
  // GA4 page_view
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: path,
    });
  }

  // Facebook Pixel PageView
  if (typeof window.fbq === 'function') {
    window.fbq('track', 'PageView');
  }
}

/* ---------- Order conversion tracking ---------- */

/**
 * Fires order submission conversion events on GA4 and Facebook Pixel.
 * Call once when the order confirmation page mounts.
 */
export function trackOrderSubmitted(
  referenceNumber: string,
  totalPrice: number,
): void {
  // GA4: standard purchase event for conversion tracking
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'purchase', {
      transaction_id: referenceNumber,
      value: totalPrice,
      currency: 'USD',
    });
    // Also fire custom event for custom reporting
    window.gtag('event', 'order_submitted', {
      transaction_id: referenceNumber,
      value: totalPrice,
      currency: 'USD',
    });
  }

  // Facebook Pixel: standard Purchase event for conversion tracking
  if (typeof window.fbq === 'function') {
    window.fbq('track', 'Purchase', {
      value: totalPrice,
      currency: 'USD',
      content_ids: [referenceNumber],
    });
  }
}

/* ---------- Payment conversion tracking ---------- */

/**
 * Fires payment completion conversion events on GA4 and Facebook Pixel.
 * Call once when the payment success page mounts.
 */
export function trackPaymentCompleted(
  referenceNumber: string,
  totalPrice: number,
  paymentMethod: string,
): void {
  // GA4: payment_completed event for conversion tracking
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'payment_completed', {
      transaction_id: referenceNumber,
      value: totalPrice,
      currency: 'USD',
      payment_type: paymentMethod,
    });
  }

  // Facebook Pixel: custom PaymentCompleted event to avoid double-counting with Purchase
  if (typeof window.fbq === 'function') {
    window.fbq('trackCustom', 'PaymentCompleted', {
      value: totalPrice,
      currency: 'USD',
      content_ids: [referenceNumber],
      payment_method: paymentMethod,
    });
  }
}

/* ---------- Initialization ---------- */

/**
 * Initializes all analytics providers. Call once at app startup.
 */
export function initAnalytics(): void {
  initGA4();
  initFBPixel();
}

/* ---------- Reset (for testing) ---------- */

/**
 * Resets initialization flags. Only for use in tests.
 */
export function _resetAnalytics(): void {
  ga4Initialized = false;
  fbPixelInitialized = false;
}
